import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useId } from 'react';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { DayOfMonthPicker } from './DayOfMonthPicker';
import { LabelledRadioGroup } from '../LabelledRadioGroup/LabelledRadioGroup';
import { SCHEDULER_SELECT_CLASS } from './schedulerFieldClasses';
import {
  DAYS_SOME_MONTHS_LACK,
  LAST,
  MonthlyRepeat,
  ordinal,
  ParsedScheduleRule,
  resolvedDayOfMonth,
  resolvedMonthWeek,
  ScheduleForm,
  weekdayName,
  WEEKDAYS,
  weekOfMonthLabel,
  WEEKS_OF_MONTH
} from './scheduleRule';

const REPEAT_OPTIONS: { label: string; value: MonthlyRepeat }[] = [
  { label: 'Day of month', value: 'dayOfMonth' },
  { label: 'Weekday of month', value: 'weekdayOfMonth' }
];

// Last sorts after every numbered week.
const inListOrder = (a: number, b: number) => (a === LAST ? 99 : a) - (b === LAST ? 99 : b);

// A saved value outside the standard list stays selectable, so saving that schedule unchanged keeps it.
const withSaved = (values: number[], saved: number | null | undefined): number[] =>
  saved === null || saved === undefined || values.includes(saved) ? values : [...values, saved].sort(inListOrder);

export interface MonthlyRepeatFieldsProps {
  viewKey: string;
  form: ScheduleForm;
  saved: ParsedScheduleRule | null;
  disabled: boolean;
  onChange: (patch: Partial<ParsedScheduleRule>) => void;
}

/** A monthly schedule's Repeat on choice, with its day-of-month picker or its week and weekday selects. */
export const MonthlyRepeatFields = ({ viewKey, form, saved, disabled, onChange }: MonthlyRepeatFieldsProps) => {
  const day = resolvedDayOfMonth(form);
  const byDay = form.monthlyRepeat === 'dayOfMonth';
  const fallsBack = byDay && DAYS_SOME_MONTHS_LACK.includes(day);
  // A view key has spaces, and aria-describedby reads a space-separated list of ids.
  const noteId = useId();
  const weeks = withSaved(WEEKS_OF_MONTH, saved?.monthWeek);
  const weekdays = withSaved(
    WEEKDAYS.map((option) => option.value),
    saved?.weekday
  );

  return (
    <div className="mb-3">
      <LabelledRadioGroup
        label="Repeat on"
        name={`repeat-on-${viewKey}`}
        options={REPEAT_OPTIONS}
        value={form.monthlyRepeat}
        disabled={disabled}
        onChange={(monthlyRepeat) => onChange({ monthlyRepeat })}
      />
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <TextSpan
          size="sm"
          color="grey6">
          On the
        </TextSpan>
        {byDay ? (
          <DayOfMonthPicker
            value={day}
            disabled={disabled}
            describedBy={fallsBack ? noteId : undefined}
            onChange={(dayOfMonth) => onChange({ dayOfMonth })}
          />
        ) : (
          <>
            <select
              aria-label="Week of month"
              className={SCHEDULER_SELECT_CLASS}
              value={resolvedMonthWeek(form)}
              disabled={disabled}
              onChange={(e) => onChange({ monthWeek: Number(e.target.value) })}>
              {weeks.map((week) => (
                <option
                  key={week}
                  value={week}>
                  {weekOfMonthLabel(week)}
                </option>
              ))}
            </select>
            <select
              aria-label="Day of the week"
              className={SCHEDULER_SELECT_CLASS}
              value={form.weekday}
              disabled={disabled}
              onChange={(e) => onChange({ weekday: Number(e.target.value) })}>
              {weekdays.map((weekday) => (
                <option
                  key={weekday}
                  value={weekday}>
                  {weekdayName(weekday)}
                </option>
              ))}
            </select>
          </>
        )}
        <TextSpan
          size="sm"
          color="grey6">
          {form.interval === 1 ? 'of every month' : `of every ${ordinal(form.interval)} month`}
        </TextSpan>
      </div>
      {fallsBack && (
        <div
          id={noteId}
          role="note"
          className="flex items-start gap-2 mt-2 rounded bg-amber-50 px-3 py-2">
          <ErrorOutlineIcon
            fontSize="small"
            className="text-amber-700"
          />
          <TextBlock
            size="xs"
            color="grey9">
            In months with fewer days, this report sends on the last day of the month.
          </TextBlock>
        </div>
      )}
    </div>
  );
};
